import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { css } from '@emotion/react';
import { pendo } from '@frontend/tracking';
import { useOnboardingShallowStore } from '../hooks/use-onboarding-store';
import type { DotPosition, OffsetStyle } from '../types';
import { dotPositionStyles } from './dot-position-styles';
import { HotspotNudge } from './hotspot-nudge';

interface OnboardingHotspotProps {
  target: string;
  enabled?: boolean;
  position?: DotPosition;
  offset?: OffsetStyle;
  children: ReactNode;
}

export function OnboardingHotspot({ enabled = true, children, ...props }: OnboardingHotspotProps) {
  if (!enabled) {
    return <>{children}</>;
  }

  return <OnboardingHotspotContent {...props}>{children}</OnboardingHotspotContent>;
}

function OnboardingHotspotContent({
  target,
  position = 'top-right',
  offset,
  children,
}: Omit<OnboardingHotspotProps, 'enabled'>) {
  const { mountTarget, unmountTarget, activeHotspot, activeInfoHotspots, completeHotspot } = useOnboardingShallowStore(
    'mountTarget',
    'unmountTarget',
    'activeHotspot',
    'activeInfoHotspots',
    'completeHotspot'
  );

  const instanceId = useId();
  const trackedSeenHotspotKeyRef = useRef<string | undefined>();

  useEffect(() => {
    mountTarget(target, instanceId);
    return () => unmountTarget(instanceId);
  }, [target, instanceId, mountTarget, unmountTarget]);

  const infoHotspotForTarget = activeInfoHotspots.find((hotspot) => hotspot.target === target);
  const infoHotspotKey = infoHotspotForTarget?.key;

  const hasActiveGuidedHotspot = activeHotspot?.target === target && activeHotspot?.instanceId === instanceId;

  const hotspotInteractionTrackingId = hasActiveGuidedHotspot
    ? activeHotspot?.tracking?.interactionTrackingId
    : undefined;

  const handleHotspotComplete = useCallback(() => {
    if (!hasActiveGuidedHotspot || !activeHotspot) return;

    completeHotspot(activeHotspot.key);
  }, [hasActiveGuidedHotspot, activeHotspot, completeHotspot]);

  useEffect(() => {
    if (infoHotspotKey) completeHotspot(infoHotspotKey);
  }, [infoHotspotKey, completeHotspot]);

  /**
   * Track "hotspot seen" when this guided hotspot becomes active.
   * This does not complete the hotspot. Completion still happens only when the user interacts with the target element.
   */
  useEffect(() => {
    if (!hasActiveGuidedHotspot || !activeHotspot) return;

    const seenEvent = activeHotspot.tracking?.seenEvent;

    if (!seenEvent) return;
    if (trackedSeenHotspotKeyRef.current === activeHotspot.key) return;

    trackedSeenHotspotKeyRef.current = activeHotspot.key;

    pendo.track(seenEvent, {
      hotspotKey: activeHotspot.key,
      target: activeHotspot.target,
      date: new Date().toLocaleString(),
    });
  }, [hasActiveGuidedHotspot, activeHotspot]);

  return (
    <div css={styles.wrapper} onClick={handleHotspotComplete} data-trackingid={hotspotInteractionTrackingId}>
      {children}
      {hasActiveGuidedHotspot && (
        <HotspotNudge className='onboarding-hotspot-nudge' css={dotPositionStyles[position]} offset={offset} />
      )}
    </div>
  );
}

const styles = {
  wrapper: css({
    position: 'relative',
    display: 'inline',
  }),
};
